/*
 * pos-main-client.js  (ES5 + jQuery 3, load after terminal-pos.js)
 * Requests from any POS to the organizer's ONE main POS, over the shared POS socket (feature 'main').
 * Which device is main is decided by the backend when it issues the token; the server routes,
 * so senders never address the main POS themselves.
 *
 * Request kinds are free-form ('print', and whatever you add later): register a handler on the
 * main POS and send that kind from the others. The server needs no change for a new kind.
 *
 * ---- Any POS: send ---------------------------------------------------------------------
 *   SocketHub.main.send(kind, payload, opts) -> jQuery promise
 *     .done(reply)   reply = {status:'success', data, reason, from, reqId}
 *     .fail(err)     err   = {code, reason, data, reqId, kind, uncertain}
 *        code: not_initialized | not_enabled | channel_offline | bad_request | send_failed
 *              main_offline (never delivered: safe to retry)
 *              failed | rejected (the main POS answered; reason says why, e.g. no_handler)
 *              timeout | main_lost (uncertain: true, it MAY have run, check before retrying)
 *              stopped | not_logged_in | replaced
 *     opts: timeout (ms, default 15000), reqId (reuse it to retry the SAME request: the main POS
 *           answers a repeated reqId from memory instead of running it again)
 *     The promise carries .reqId.
 *
 * ---- Main POS: handle --------------------------------------------------------------------
 *   SocketHub.main.handle(kind, function (payload, ctx) { ... })
 *     ctx: {reqId, kind, from, progress(stage)}
 *     return: nothing / true          -> success
 *             false                   -> failed
 *             {status, data, reason}  -> as given (status: success | failed | rejected)
 *             any other value         -> success with that value as data
 *             a promise of the above  -> its result; a rejected promise -> failed
 *   Each reqId runs ONCE; its answer is remembered (last 100, in localStorage) for resends.
 *   Started automatically on pages with <body is_main_pos="1">.
 *
 * ---- Changing the main POS ---------------------------------------------------------------
 *   SocketHub.main.assign(token) -> jQuery promise
 *     token = main_assign_token returned by the backend when it saved the new main POS
 *     (organizer_settings/update_main_pos_device). The server switches the live role at once and tells
 *     EVERY POS of the organizer (main:presence for all, main:role for the old and the new main).
 *     .done({id, online})   online = the new main POS is connected right now
 *     .fail({code})         invalid_assign | timeout | not_initialized | channel_offline | send_failed | ...
 *
 * ---- Both --------------------------------------------------------------------------------
 *   SocketHub.main.isMain()  .online()  .mainId()  .pending()
 *   jQuery events on document:
 *     main:presence {online, id, self}   main:role {main}   main:progress {reqId, stage}
 *     main:request  {reqId, kind, from}  (main side, kind without a handler)
 *
 * Example, pull printing:
 *   // main POS
 *   SocketHub.main.handle('print', function (p) { return printOnLocalPrinter(p.document, p.printer); });
 *   // any POS
 *   SocketHub.main.send('print', { document: html, printer: 'kitchen' })
 *     .done(function () { showToast({type: 'success', message: 'Sendt til hovedkassen'}); })
 *     .fail(function (e) { ... e.uncertain ? 'check the printer first' : 'try again' ... });
 */
(function (window, $) {
  'use strict';
  var T = window.SocketHub,
    M = T.MSG;
  var DONE_STORE = 'terminal.main.done',
    DONE_MAX = 100,
    DONE_DATA_MAX = 4096,
    RESEND_MS = [4000, 8000],
    DEFAULT_TIMEOUT = 15000;
  var STATUSES = { success: true, failed: true, rejected: true };

  var online = false,
    mainId = null,
    self = false,
    selfId = null,
    assigning = null; // {d, timer}
  var reqs = {}; // sender: reqId -> {reqId, kind, payload, d, tries, accepted, timers}
  var handlers = {}, // main: kind -> fn
    running = {}, // main: reqId -> true
    done = {}, // main: reqId -> {status, reason, data}
    doneList = [];

  function fire(name, data) {
    $(document).trigger('main:' + name, [data]);
  }
  function presenceInfo() {
    return { online: online, id: mainId, self: self };
  }

  // ======================================================================================
  // sender
  // ======================================================================================
  function settle(r, ok, value) {
    if (reqs[r.reqId] !== r) return;
    delete reqs[r.reqId];
    $.each(r.timers, function (i, t) {
      clearTimeout(t);
    });
    if (ok) r.d.resolve(value);
    else r.d.reject(value);
  }

  function fail(r, code, extra) {
    settle(
      r,
      false,
      $.extend(
        {
          code: code,
          reason: null,
          data: null,
          reqId: r.reqId,
          kind: r.kind,
          uncertain: code === 'timeout' || code === 'main_lost',
        },
        extra
      )
    );
  }

  function transmit(r) {
    if (
      T._posSend(M.MAIN_REQUEST, {
        reqId: r.reqId,
        kind: r.kind,
        payload: r.payload,
      })
    ) {
      r.tries++;
      return true;
    }
    return false;
  }

  function send(kind, payload, opts) {
    opts = opts || {};
    var d = $.Deferred(),
      p = d.promise();
    function no(code) {
      d.reject({
        code: code,
        reason: null,
        data: null,
        reqId: null,
        kind: kind,
        uncertain: false,
      });
      return p;
    }
    if (!T.pos || !T.pos.started()) return no('not_initialized');
    if (!T._posHas('main')) return no('not_enabled');
    if (!T._posReady()) return no('channel_offline');
    if (typeof kind !== 'string' || !/^[a-z][a-z0-9_.]{0,31}$/.test(kind))
      return no('bad_request');
    if (!online) return no('main_offline');

    var reqId = opts.reqId || T.newId();
    if (reqs[reqId]) return reqs[reqId].p; // same request already running

    var r = {
      reqId: reqId,
      kind: kind,
      payload: payload === undefined ? null : payload,
      d: d,
      p: p,
      tries: 0,
      accepted: false,
      timers: [],
    };
    p.reqId = reqId;
    reqs[reqId] = r;
    if (!transmit(r)) {
      delete reqs[reqId];
      return no('send_failed');
    }

    // no sign of life: resend the SAME reqId (the main POS never runs it twice)
    $.each(RESEND_MS, function (i, ms) {
      r.timers.push(
        setTimeout(function () {
          if (reqs[r.reqId] === r && !r.accepted && T._posReady()) transmit(r);
        }, ms)
      );
    });
    r.timers.push(
      setTimeout(function () {
        fail(r, 'timeout');
      }, opts.timeout || DEFAULT_TIMEOUT)
    );
    return p;
  }

  // ======================================================================================
  // main POS
  // ======================================================================================
  (function loadDone() {
    try {
      var a = JSON.parse(localStorage.getItem(DONE_STORE) || '[]');
      if ($.isArray(a))
        $.each(a, function (i, e) {
          if (e && typeof e.id === 'string' && e.res) {
            doneList.push(e);
            done[e.id] = e.res;
          }
        });
    } catch (e) {}
  })();

  function remember(id, res) {
    var keep = { status: res.status, reason: res.reason || null, data: null };
    try {
      if (
        res.data !== undefined &&
        JSON.stringify(res.data).length <= DONE_DATA_MAX
      )
        keep.data = res.data;
    } catch (e) {}
    done[id] = keep;
    doneList.push({ id: id, res: keep });
    while (doneList.length > DONE_MAX) delete done[doneList.shift().id];
    try {
      localStorage.setItem(DONE_STORE, JSON.stringify(doneList));
    } catch (e) {}
  }

  function normalize(v) {
    if (v === undefined || v === null || v === true)
      return { status: 'success', reason: null, data: null };
    if (v === false) return { status: 'failed', reason: null, data: null };
    if (typeof v === 'object' && typeof v.status === 'string')
      return {
        status: STATUSES[v.status] ? v.status : 'failed',
        reason: v.reason || null,
        data: v.data === undefined ? null : v.data,
      };
    return { status: 'success', reason: null, data: v };
  }

  function reply(m, res) {
    T._posSend(M.MAIN_REPLY, {
      reqId: m.reqId,
      status: res.status,
      reason: res.reason || null,
      data: res.data === undefined ? null : res.data,
    });
  }

  function finishRequest(m, res) {
    delete running[m.reqId];
    remember(m.reqId, res);
    reply(m, res); // if this is lost, the sender resends and gets it from `done`
  }

  function onRequest(m) {
    if (typeof m.reqId !== 'string' || typeof m.from !== 'string') return;
    if (done[m.reqId]) {
      reply(m, done[m.reqId]); // resend: answer from memory, do not run again
      return;
    }
    if (running[m.reqId]) {
      T._posSend(M.MAIN_PROGRESS, { reqId: m.reqId, stage: 'running' });
      return;
    }
    var h = handlers[m.kind];
    if (typeof h !== 'function') {
      fire('request', { reqId: m.reqId, kind: m.kind, from: m.from });
      finishRequest(m, {
        status: 'rejected',
        reason: 'no_handler',
        data: null,
      });
      return;
    }

    running[m.reqId] = true;
    T._posSend(M.MAIN_PROGRESS, { reqId: m.reqId, stage: 'accepted' });
    var ctx = {
      reqId: m.reqId,
      kind: m.kind,
      from: m.from,
      progress: function (stage) {
        if (running[m.reqId])
          T._posSend(M.MAIN_PROGRESS, { reqId: m.reqId, stage: String(stage) });
      },
    };
    var out;
    try {
      out = h(m.payload, ctx);
    } catch (e) {
      console.error('[main] handler for ' + m.kind + ' threw', e);
      finishRequest(m, {
        status: 'failed',
        reason: 'handler_error',
        data: null,
      });
      return;
    }
    $.when(out).then(
      function (v) {
        finishRequest(m, normalize(v));
      },
      function (e) {
        finishRequest(m, {
          status: 'failed',
          reason:
            (e && typeof e.reason === 'string' && e.reason) ||
            (typeof e === 'string' ? e : 'handler_error'),
          data: null,
        });
      }
    );
  }

  // ======================================================================================
  // socket module
  // ======================================================================================
  T._posModule('main', {
    onReady: function (w) {
      var mm = (w && w.main) || {};
      selfId = w && w.id ? String(w.id) : null;
      online = !!mm.online;
      mainId = mm.id || null;
      self = !!mm.self;
      fire('presence', presenceInfo());
      fire('role', { main: self });
      // a reply may have been lost while we were away: ask again with the same reqId
      if (online)
        $.each(reqs, function (id, r) {
          transmit(r);
        });
    },
    onMessage: function (m) {
      var r = m.reqId ? reqs[m.reqId] : null;
      switch (m.type) {
        case M.MAIN_REPLY:
          if (!r) break;
          if (m.status === 'success')
            settle(r, true, {
              status: 'success',
              data: m.data === undefined ? null : m.data,
              reason: m.reason || null,
              from: m.from,
              reqId: m.reqId,
            });
          else
            fail(r, STATUSES[m.status] ? m.status : 'failed', {
              reason: m.reason || null,
              data: m.data === undefined ? null : m.data,
            });
          break;
        case M.MAIN_PROGRESS:
          if (r) r.accepted = true;
          fire('progress', { reqId: m.reqId, stage: m.stage });
          break;
        case M.MAIN_ERROR:
          if (!m.reqId && assigning) {
            settleAssign(false, { code: m.code || 'failed' });
            break;
          }
          if (!r) break;
          // a RESEND refused as main_offline means the first copy may have arrived
          fail(
            r,
            m.code === 'main_offline' && r.tries > 1 ? 'main_lost' : m.code
          );
          break;
        case M.MAIN_LOST:
          if (r) fail(r, 'main_lost');
          break;
        case M.MAIN_PRESENCE:
          var wasSelf = self;
          online = !!m.online;
          mainId = m.id || null;
          self = mainId !== null && mainId === selfId;
          fire('presence', presenceInfo());
          if (self !== wasSelf) fire('role', { main: self });
          break;
        case M.MAIN_ROLE:
          if (self !== !!m.main) {
            self = !!m.main;
            fire('role', { main: self });
          }
          break;
        case M.MAIN_ASSIGNED:
          settleAssign(true, { id: m.id, online: !!m.online });
          break;
        case M.MAIN_REQUEST:
          onRequest(m);
          break;
      }
    },
    onClose: function () {
      settleAssign(false, { code: 'channel_offline' });
      online = false; // pending requests keep their own timeout
      fire('presence', presenceInfo());
    },
    onStop: function (code) {
      $.each(reqs, function (id, r) {
        fail(r, code || 'stopped');
      });
      online = false;
      self = false;
      fire('presence', presenceInfo());
    },
    busy: function () {
      return !$.isEmptyObject(reqs) || !$.isEmptyObject(running);
    },
  });

  function assign(token) {
    var d = $.Deferred(),
      p = d.promise();
    function no(code) {
      d.reject({ code: code });
      return p;
    }
    if (!T.pos || !T.pos.started()) return no('not_initialized');
    if (!T._posHas('main')) return no('not_enabled');
    if (!T._posReady()) return no('channel_offline');
    if (typeof token !== 'string' || !token) return no('bad_request');
    if (assigning) return assigning.p; // one at a time: same answer for both callers

    var a = { d: d, p: p, timer: null };
    assigning = a;
    a.timer = setTimeout(function () {
      settleAssign(false, { code: 'timeout' });
    }, 8000);
    if (!T._posSend(M.MAIN_ASSIGN, { token: token })) {
      settleAssign(false, { code: 'send_failed' });
    }
    return p;
  }
  function settleAssign(ok, value) {
    var a = assigning;
    if (!a) return;
    assigning = null;
    clearTimeout(a.timer);
    if (ok) a.d.resolve(value);
    else a.d.reject(value);
  }

  T.main = {
    send: send,
    assign: assign,
    handle: function (kind, fn) {
      handlers[kind] = fn;
    },
    unhandle: function (kind) {
      delete handlers[kind];
    },
    isMain: function () {
      return self;
    },
    online: function () {
      return online;
    },
    mainId: function () {
      return mainId;
    },
    pending: function () {
      return $.map(reqs, function (r, id) {
        return id;
      });
    },
  };

  // ---- boot: the main POS stays reachable on every page ---------------------------------
  $(function () {
    var $b = $('body');
    if (OnDevice.active() || $b.attr('is_customer_screen') === '1') return;
    if ($b.attr('is_main_pos') === '1') {
      T.ensurePos({ features: ['main'] }).fail(function (code) {
        console.warn('[main] channel not started:', code);
      });
    }
  });
})(window, jQuery);
